import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthDispatch } from "../provider/AuthProvider";
import { useAsyncAxios, utilAxiosWithAppToken } from "../utils/customAxios";
import { CookiesProvider, useCookies } from "react-cookie";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { Button, Checkbox, Form, Input, Card, Divider, Col, Row } from "antd";
import { useAlert } from "../provider/AlertProvider";
import Notification from "../components/Notification";

import * as Request from "../commons/request";
import * as String from "../commons/string";
import * as Common from "../commons/common";
import * as Utils from "../utils/utils";

import styles from "./Login.module.css";

function Login() {
    const dispatch = useAuthDispatch();
    const navigate = useNavigate();
    const alert = useAlert();

    const [user, setUser] = useState<string>("");
    const [pwd, setPwd] = useState<string>("");
    const [checkedSaveId, setCheckedSaveId] = useState<boolean>(false);
    const [cookies, setCookie, removeCookie] = useCookies(["savedId"]);
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [isCapsLock, setIsCapsLock] = useState(false);

    const requestLogin = async (url: string, option: Record<string, string>) => {
        const response = await utilAxiosWithAppToken().post(url, option);
        return response.data;
    };

    const { loading, error: errorLogin, data: resultLogin, execute: login } = useAsyncAxios(requestLogin);

    const onChangeSaveId = (e: CheckboxChangeEvent) => {
        if (e.target.checked) {
            setCheckedSaveId(true);
        } else {
            setCheckedSaveId(false);
        }
    };

    useEffect(() => {
        if (cookies.savedId) {
            setUser(cookies.savedId);
        }
    }, []);

    useEffect(() => {
        if (resultLogin === null) return;

        if (
            resultLogin.user.authority !== "ROLE_ADMIN" &&
            resultLogin.user.authority !== "ROLE_MANAGER" &&
            resultLogin.user.authority !== "ROLE_RENT_MANAGER" &&
            resultLogin.user.authority !== "ROLE_B2B_MANAGER"
        ) {
            if (dispatch !== null)
                dispatch({
                    type: "LOGOUT",
                });

            alert.setAlert("", "로그인 실패", String.msg_admin_must);

            return;
        }

        if (dispatch !== null)
            dispatch({
                type: "LOGIN_SUCCESS",
                payload: {
                    user: resultLogin.user,
                    token: resultLogin.token,
                },
            });

        const expire = new Date();
        expire.setDate(expire.getDate() + 30);
        if (checkedSaveId === true && resultLogin.result === "ok") {
            setCookie("savedId", user, { expires: expire });
        } else {
            removeCookie("savedId");
        }
        if (resultLogin.result === "ok") {
            navigate(resultLogin.user.authority === "ROLE_B2B_MANAGER" ? Common.PAGE_LIST_RESERVATION : Common.PAGE_DASHBOARD);
        }

        setPwd("");
    }, [resultLogin]);

    useEffect(() => {
        if (errorLogin === null) return;

        if (dispatch !== null) dispatch({ type: "LOGIN_ERROR", error: "Login failed" });

        // console.log("errorLogin", errorLogin);

        alert.setAlert(errorLogin.response?.data?.code, "로그인 실패", errorLogin.response?.data?.message);
    }, [errorLogin]);

    const actionLogin = async () => {
        await login(Request.LOGIN_URL, JSON.stringify({ email: user, password: pwd }));
    };

    const openPopup = () => {
        setShowPopup(true);
    };

    const closePopup = () => {
        setShowPopup(false);
    };

    const validatePwd = useCallback((_: any, value: string) => {
        const pwd_regExp = /^[a-zA-Z0-9#?!@$%^&*-]*$/i;
        if (!value) {
            return Promise.reject(new Error(String.msgPwdMust));
        }
        if (!pwd_regExp.test(value)) {
            return Promise.reject(new Error(String.msgPwdRule));
        }
        return Promise.resolve();
    }, []);

    function onKeyDownHandler(e: any) {
        const checkCapsLock = e.getModifierState("CapsLock");
        setIsCapsLock(checkCapsLock);
    }

    return (
        <Row align="middle" justify="center">
            <Col id={styles.right} span={12}>
                <Card className={styles.loginCard} bordered={false}>
                    <Divider className={styles.loginTitleArea}>
                        <p>{String.titleLogin}</p>
                    </Divider>
                    <Form name="normal_login" onFinish={actionLogin} initialValues={{ remember: true }}>
                        <CookiesProvider>
                            <Form.Item
                                name="username"
                                rules={[
                                    {
                                        validator: (_, value) => Utils.validateEmail(true, false, value),
                                    },
                                ]}
                                initialValue={cookies.savedId ? cookies.savedId : ""}
                            >
                                <Input
                                    className={styles.loginInput}
                                    prefix={<UserOutlined className="site-form-item-icon" />}
                                    placeholder={String.id}
                                    maxLength={100}
                                    onChange={(e) => setUser(e.target.value)}
                                />
                            </Form.Item>
                        </CookiesProvider>
                        <Form.Item name="password" rules={[{ validator: validatePwd }, { min: 8, message: String.msgPwdMin }]}>
                            <Input
                                className={styles.loginInput}
                                prefix={<LockOutlined className="site-form-item-icon" />}
                                type="password"
                                placeholder={String.password}
                                maxLength={20}
                                onChange={(e) => setPwd(e.target.value)}
                                onKeyDown={onKeyDownHandler}
                                suffix={isCapsLock ? "Caps Lock이 켜져 있습니다." : ""}
                            />
                        </Form.Item>
                        <Form.Item>
                            <Checkbox
                                className={styles.flexBetweenLeft}
                                onChange={(e) => {
                                    onChangeSaveId(e);
                                }}
                                defaultChecked={cookies.savedId ? true : false}
                            >
                                {String.saveId}
                            </Checkbox>
                        </Form.Item>
                        <Form.Item>
                            <Button
                                className={styles.loginInput}
                                block
                                type="primary"
                                htmlType="submit"
                                size="large"
                                disabled={
                                    user.length >= 5 && pwd.length >= 8
                                        ? // TODO: VALIDATE 성공 여부 추가
                                          false
                                        : true
                                }
                            >
                                {String.login}
                            </Button>
                        </Form.Item>
                    </Form>
                    <Notification
                        open={showPopup}
                        close={closePopup}
                        content="ID 또는 비밀번호를 잘못 입력하셨습니다. 연속 5회 이상 로그인 실패 시 이용이 제한됩니다."
                    />
                </Card>
            </Col>
        </Row>
    );
}

export default Login;
